import { day } from './time';

/**
 * Тариф и лимиты словами (T11.10, `05_API.md` §4).
 *
 * Сервер отдаёт числа и коды: `free`, `sent_in_period: 7`, `resets_at`,
 * `delay_seconds: 900`. Человеку нужны слова, и складываются они здесь,
 * а не в разметке экрана: тогда их можно проверить тестом, а показ
 * остаётся показом.
 *
 * ## Почему лимит не показывается долей
 *
 * «70 %» не говорит, сколько осталось, а осталось — единственное, что
 * человеку нужно. Показывается «7 из 10»; полоса рядом даёт то же
 * знание взглядом, не заменяя числа.
 */

/** Ответ `GET /v1/users/me/limits`. */
export interface Limits {
  readonly plan: string;
  readonly plan_expires_at: string | null;
  readonly subscriptions: { used: number; limit: number };
  readonly notifications: {
    sent_in_period: number;
    limit: number;
    resets_at: string;
    delay_seconds: number;
  };
  readonly features: Record<string, boolean>;
  readonly price_history_days: number | null;
}

/** Ответ `GET /v1/users/me`. */
export interface Profile {
  readonly public_id: string;
  readonly username: string | null;
  readonly first_name: string | null;
  readonly language: string;
  readonly timezone: string;
  readonly role: string;
  readonly plan: string;
  readonly plan_expires_at: string | null;
  readonly created_at: string;
}

/** Названия тарифов из таблицы `plans`. */
const PLAN_NAMES: Record<string, string> = {
  free: 'Free',
  pro: 'Pro',
  business: 'Business',
};

export function planName(code: string): string {
  return PLAN_NAMES[code] ?? code;
}

/**
 * Что закрыто на бесплатном тарифе.
 *
 * Названия те же, что в `modules/users/quotas.ts`: перечень приходит
 * оттуда, и незнакомый ключ показывается как есть — молча пропущенная
 * функция выглядела бы доступной.
 */
const FEATURE_NAMES: Record<string, string> = {
  market_delta_condition: 'подписка с условием «ниже рынка»',
  cross_source_comparison: 'сравнение цен по площадкам',
  full_price_history: 'полная история цены',
};

export function featureName(code: string): string {
  return FEATURE_NAMES[code] ?? code;
}

/** Заперто на действующем тарифе. Порядок ответа сохраняется. */
export function lockedFeatures(features: Record<string, boolean>): string[] {
  return Object.entries(features)
    .filter(([, allowed]) => !allowed)
    .map(([code]) => featureName(code));
}

/** «7 из 10». Ноль в пределе означает «не положено вовсе». */
export function quota(used: number, limit: number): string {
  return limit === 0 ? 'не входит в тариф' : `${used} из ${limit}`;
}

/**
 * Доля заполнения для полосы, 0…1.
 *
 * Ограничена сверху: потребление выше предела бывает — тариф истёк, а
 * подписки остались, — и полоса, вылезающая за карточку, выглядит
 * поломкой вместо того, чтобы говорить «предел превышен».
 */
export function fill(used: number, limit: number): number {
  if (limit <= 0) return 1;

  return Math.max(0, Math.min(1, used / limit));
}

/**
 * Срок действия тарифа.
 *
 * Пусто — тариф бессрочный: так выглядит `free` и так же выглядел бы
 * выданный вручную без срока (T12.5). Истёкший назван истёкшим:
 * «действует до вчера» человек прочитает как «действует».
 */
export function expiry(expiresAt: string | null, now: Date): string | null {
  if (expiresAt === null) return null;

  const moment = new Date(expiresAt);

  if (Number.isNaN(moment.getTime())) return null;

  return moment.getTime() <= now.getTime()
    ? `истёк ${day(moment, now)}`
    : `действует до ${day(moment, now)}`;
}

/** Русское склонение при числе: «1 день», «2 дня», «5 дней». */
function plural(count: number, one: string, few: string, many: string): string {
  const tens = count % 100;
  const ones = count % 10;

  if (tens >= 11 && tens <= 14) return many;
  if (ones === 1) return one;
  if (ones >= 2 && ones <= 4) return few;

  return many;
}

/**
 * Когда обнулится квота.
 *
 * Сроком, а не временем: граница периода считается по часовому поясу
 * пользователя, а не устройства, и «1 октября в 00:00» на телефоне,
 * увезённом в другой пояс, было бы неправдой. «Через 12 дней» верно
 * везде.
 *
 * Единица меняется вместе с расстоянием. Квота стала месячной (13
 * сентября 2026), и часы, годившиеся для суточной, превратились в
 * «обнулится через 600 ч» — число, из которого человек ничего не
 * извлечёт, не поделив его на двадцать четыре.
 *
 * Порог в двое суток выбран не круглым числом: до двух суток «через 30
 * часов» точнее, чем «через день», а дальше точность уже не нужна —
 * важно, скоро это или нет.
 */
export function resets(resetsAt: string, now: Date): string | null {
  const moment = new Date(resetsAt);

  if (Number.isNaN(moment.getTime())) return null;

  const minutes = Math.round((moment.getTime() - now.getTime()) / 60_000);

  if (minutes <= 0) return 'обнуление вот-вот';
  if (minutes < 60) return `обнулится через ${String(minutes)} мин`;

  const hours = Math.round(minutes / 60);

  if (hours < 48)
    return `обнулится через ${String(hours)} ${plural(hours, 'час', 'часа', 'часов')}`;

  const days = Math.round(hours / 24);

  return `обнулится через ${String(days)} ${plural(days, 'день', 'дня', 'дней')}`;
}

/**
 * Задержка доставки — то, за что платят (`01_PRODUCT.md` §7).
 *
 * Названа всегда, даже когда её нет: «доставка сразу» на платном
 * тарифе — это и есть купленное, и увидеть его человек должен там же,
 * где увидел бы задержку.
 */
export function delay(seconds: number): string {
  if (seconds <= 0) return 'доставка сразу';
  if (seconds < 60) return `задержка ${String(seconds)} с`;

  return `задержка ${String(Math.round(seconds / 60))} мин`;
}

/** Тариф в том виде, в каком его отдаёт `GET /v1/plans` (T12.6). */
export interface Offer {
  readonly code: string;
  readonly name: string;
  readonly price_minor: number | null;
  readonly currency: string;
  readonly max_subscriptions: number;
  readonly max_notifications_per_month: number;
  readonly notification_delay_seconds: number;
}

/**
 * Цена словами.
 *
 * Рублями без копеек: цена тарифа круглая, и «690,00 ₽» читается
 * медленнее, чем «690 ₽», не сообщая ничего сверх.
 */
export function priceMonthly(minor: number | null): string {
  if (minor === null || minor === 0) return 'бесплатно';

  return `${Math.round(minor / 100).toLocaleString('ru-RU')} ₽ в месяц`;
}

/** «10 минут», «сразу» — задержка доставки словами. */
function waiting(seconds: number): string {
  if (seconds <= 0) return 'сразу';
  if (seconds < 60) return `через ${String(seconds)} с`;

  return `через ${String(Math.round(seconds / 60))} мин`;
}

/**
 * Чем платный тариф отличается от бесплатного.
 *
 * Строится из ответа сервера, а не выписывается в разметке: лимиты
 * живут в таблице `plans` и меняются миграцией, а обещание на экране
 * оплаты, разошедшееся с тем, что человек получит, — это обман, за
 * который платят деньгами.
 *
 * Первым идёт различие в задержке: оно и есть то, что продаётся
 * (`01_PRODUCT.md` §7). Дальше — числа, и только те, где платный тариф
 * действительно щедрее: одинаковые строки в сравнении лишь разбавляют
 * важное.
 */
export function advantages(paid: Offer, free: Offer | undefined): string[] {
  const rows: string[] = [];
  const freeDelay = free?.notification_delay_seconds ?? 0;

  if (paid.notification_delay_seconds < freeDelay) {
    rows.push(
      `Уведомления ${waiting(paid.notification_delay_seconds)}, а не ${waiting(freeDelay)}`,
    );
  }

  if (free === undefined || paid.max_subscriptions > free.max_subscriptions) {
    rows.push(
      free === undefined
        ? `${String(paid.max_subscriptions)} подписок`
        : `${String(paid.max_subscriptions)} подписок вместо ${String(free.max_subscriptions)}`,
    );
  }

  if (free === undefined || paid.max_notifications_per_month > free.max_notifications_per_month) {
    rows.push(
      free === undefined
        ? `${String(paid.max_notifications_per_month)} уведомлений в месяц`
        : `${String(paid.max_notifications_per_month)} уведомлений в месяц вместо ${String(free.max_notifications_per_month)}`,
    );
  }

  return rows;
}
